import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
  Button,
  Spinner,
} from "reactstrap"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import {
  fetchData,
  fetchNodeData,
  FetchNodePostData,
} from "utils/common-fetchData" // adjust path if needed

const EMPTY_FORM = {
  companyName: "",
  address: "",
  tnc: "",
  bankName: "",
  branchName: "",
  accountName: "",
  accountNo: "",
  ifscCode: "",
}

const validateForm = (data, countryId) => {
  const e = {}
  if (!data.companyName?.trim()) e.companyName = "Company name is required."
  if (!data.address?.trim()) e.address = "Address is required."
  if (!data.tnc?.trim() || data.tnc === "<p></p>")
    e.tnc = "Terms & Conditions are required."
  if (!data.bankName?.trim()) e.bankName = "Bank name is required."
  if (!data.branchName?.trim()) e.branchName = "Branch name is required."
  if (!data.accountName?.trim()) e.accountName = "Account name is required."
  if (!data.accountNo?.trim()) e.accountNo = "Account number is required."
  else if (!/^\d{9,18}$/.test(data.accountNo.trim()))
    e.accountNo = "Enter a valid account number (9–18 digits)."
  if (countryId === 1) {
    if (!data.ifscCode?.trim()) e.ifscCode = "IFSC code is required."
    else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(data.ifscCode.trim().toUpperCase()))
      e.ifscCode = "Enter a valid IFSC code (e.g. SBIN0001234)."
  }
  return e
}

const InvoiceDetailsForm = () => {
  const [activeCountry, setActiveCountry] = useState(1)
  const [countries, setCountries] = useState([])
  const [formData, setFormData] = useState({
    1: { ...EMPTY_FORM },
    3: { ...EMPTY_FORM },
  })
  const [formErrors, setFormErrors] = useState({ 1: {}, 3: {} })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  const token = localStorage.getItem("auth_token")

  const form = formData[activeCountry]
  const errors = formErrors[activeCountry]

  useState(() => {
    ; (async () => {
      const res = await fetchData(null, "locations", token)
      setCountries(
        res?.data
          ?.filter(i => i.id != 2)
          ?.map(
            item =>
            ({
              id: item.id,
              label: item.location,
            } || [])
          )
      )
    })()
  }, [])

  // ✅ FETCH DATA
  useEffect(() => {
    const fetchData = async () => {
      const res = await fetchNodeData(
        setLoading,
        `invoiceDetails/getByCountry/${activeCountry}`,
        token
      )

      if (res?.success && res.data) {
        const data = res.data

        setFormData(prev => ({
          ...prev,
          [activeCountry]: {
            companyName: data.company_name || "",
            address: data.address || "",
            tnc: data.tnc || "",
            bankName: data.bank_name || "",
            branchName: data.branch_name || "",
            accountName: data.account_name || "",
            accountNo: data.account_no || "",
            ifscCode: data.ifsc_code || "",
          },
        }))
      }
    }

    fetchData()
  }, [activeCountry])

  const handleField = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [activeCountry]: { ...prev[activeCountry], [name]: value },
    }))
    if (errors[name])
      setFormErrors(prev => ({
        ...prev,
        [activeCountry]: { ...prev[activeCountry], [name]: undefined },
      }))
  }

  // ✅ SAVE DATA
  const handleSave = async () => {
    const errs = validateForm(form, activeCountry)
    if (Object.keys(errs).length) {
      setFormErrors(prev => ({ ...prev, [activeCountry]: errs }))
      return
    }

    const payload = {
      country_id: activeCountry,
      company_name: form.companyName,
      address: form.address,
      tnc: form.tnc,
      bank_name: form.bankName,
      branch_name: form.branchName,
      account_name: form.accountName,
      account_no: form.accountNo,
      ifsc_code: form.ifscCode,
    }

    const res = await FetchNodePostData(
      setSaving,
      "invoiceDetails",
      token,
      payload
    )

    if (res?.data?.success) {
      swal("", "Details updated successfully", "success")
    }

    if (res?.response?.status === 422) {
      setFormErrors(prev => ({
        ...prev,
        [activeCountry]: res.response.data.errors,
      }))
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-start">
          {/* Country tab buttons */}
          <div className="d-flex gap-2 mb-4">
            {countries?.map(c => (
              <button
                key={c.id}
                type="button"
                className={`btn btn-sm ${activeCountry === c.id
                    ? "btn-purple"
                    : "btn-outline-secondary"
                  }`}
                onClick={() => setActiveCountry(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="d-flex justify-content-end mt-2">
            <Button
              className={"btn-purple"}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                <>
                  <i className="fa fa-save me-2"></i>
                  Save Details
                </>
              )}
            </Button>
          </div>
        </div>

        {loading && (
          <div className="text-center mb-3">
            <Spinner size="sm" />
          </div>
        )}
        <div className="mb-3">
          <Label>
            Company Name <span className="text-danger">*</span>
          </Label>
          <Input
            placeholder="Enter Company Name"
            value={form.companyName}
            onChange={e => handleField("companyName", e.target.value)}
            invalid={!!errors.companyName}
          />
          <FormFeedback>{errors.companyName}</FormFeedback>
        </div>

        {/* Address */}
        <div className="mb-3">
          <Label>
            Address <span className="text-danger">*</span>
          </Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Enter Address"
            value={form.address}
            onChange={e => handleField("address", e.target.value)}
            invalid={!!errors.address}
          />
          <FormFeedback>{errors.address}</FormFeedback>
        </div>

        {/* Terms & Conditions */}
        <div className="mb-3">
          <Label>
            Terms & Conditions <span className="text-danger">*</span>
          </Label>
          <CKEditor
            editor={ClassicEditor}
            data={form.tnc}
            onChange={(_e, editor) => handleField("tnc", editor.getData())}
          />
          {errors.tnc && (
            <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
              {errors.tnc}
            </div>
          )}
        </div>

        <hr />
        <p
          className="text-muted fw-semibold mb-3"
          style={{
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Bank Details
        </p>

        <div className="row">
          <div className="col-md-6 mb-3">
            <Label>
              Bank Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="Enter Bank Name"
              value={form.bankName}
              onChange={e => handleField("bankName", e.target.value)}
              invalid={!!errors.bankName}
            />
            <FormFeedback>{errors.bankName}</FormFeedback>
          </div>
          {
            activeCountry === 1 &&
            <div className="col-md-6 mb-3">
              <Label>
                Branch Name <span className="text-danger">*</span>
              </Label>
              <Input
                placeholder="Enter Branch Name"
                value={form.branchName}
                onChange={e => handleField("branchName", e.target.value)}
                invalid={!!errors.branchName}
              />
              <FormFeedback>{errors.branchName}</FormFeedback>
            </div>
          }

          <div className="col-md-6 mb-3">
            <Label>
              Account Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="Enter Account Name"
              value={form.accountName}
              onChange={e => handleField("accountName", e.target.value)}
              invalid={!!errors.accountName}
            />
            <FormFeedback>{errors.accountName}</FormFeedback>
          </div>

          <div className="col-md-6 mb-3">
            <Label>
              Account No. <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="9–18 digit number"
              value={form.accountNo}
              onChange={e => handleField("accountNo", e.target.value)}
              invalid={!!errors.accountNo}
            />
            <FormFeedback>{errors.accountNo}</FormFeedback>
          </div>

          <div className="col-md-6 mb-3">
            <Label>
              {
                activeCountry === 1 ? "IFSC Code" : "Routing No."
              }
              <span className="text-danger"> *</span>
            </Label>
            <Input
              placeholder="Enter here"
              value={form.ifscCode}
              onChange={e =>
                handleField("ifscCode", e.target.value.toUpperCase())
              }
              invalid={!!errors.ifscCode}
              // maxLength={11}
            />
            <FormFeedback>{errors.ifscCode}</FormFeedback>
          </div> 
        </div>

        <div className="d-flex justify-content-end mt-2">
          <Button
            className={"btn-purple"}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <i className="fa fa-save me-2"></i>
                Save Details
              </>
            )}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

export default InvoiceDetailsForm